// <pfs-i18n-catalog>: tenant-agnostischer i18n-Catalog-Loader.
//
// Fetcht den Catalog von pfs-content (oder kompatibler API) als kompakte
// map[key]text und macht ihn globalisiert via `window.pfsI18n` verfuegbar.
// Anderer Code (WC, Templ-Page-Script, etc.) kann dann `t(key, fallback?)`
// nutzen, ohne den Fetch selbst zu kennen.
//
// Attribute:
//   data-source-url   — Catalog-Endpoint, default `/api/v1/i18n/catalog`.
//                       Locale wird als `?locale=…` Query angehaengt.
//   data-locale       — Locale-Code (z.B. "de", "en"). Default:
//                       `document.documentElement.lang || "de"`.
//
// Globaler Side-Effect:
//   window.pfsI18n = {
//     t(key, fallback?) => string,    // Lookup mit Fallback (oder key)
//     locale,                          // aktuell geladene Locale
//     messages,                        // Catalog map
//     ready                            // true sobald initial geladen
//   }
//
// Custom-Events (auf host, bubble + composed):
//   pfs:i18n:loaded   — { detail: { locale, messages } }
//   pfs:i18n:error    — { detail: { error } }
//
// Pattern: kein Lit, kein Stencil — defineComponent + einmaliger apiFetch. Ein
// reiner Einmal-Loader braucht keinen Cache/TTL (PFS-665: ResourceStore entfernt).

import { defineComponent } from "../../composition/define-component.js";
import { apiFetch } from "../../store/api-fetch.js";
import { setCatalog, PFS_I18N_LOCALE_REQUEST_EVENT } from "../../store/i18n-store.js";

const DEFAULT_SOURCE_URL = "/api/v1/i18n/catalog";
const DEFAULT_LOCALE = "de";
const LOADED_EVENT = "pfs:i18n:loaded";
const ERROR_EVENT = "pfs:i18n:error";

export interface CatalogResponse {
  tenant?: string;
  locale: string;
  messages: Record<string, string>;
}

export interface PfsI18nGlobal {
  t(key: string, fallback?: string): string;
  locale: string;
  messages: Record<string, string>;
  ready: boolean;
}

declare global {
  interface Window {
    pfsI18n?: PfsI18nGlobal;
  }
}

function readLocale(host: HTMLElement): string {
  const attr = host.getAttribute("data-locale");
  if (attr) return attr;
  const docLang = document.documentElement.lang;
  if (docLang) return docLang;
  return DEFAULT_LOCALE;
}

function readSourceUrl(host: HTMLElement): string {
  return host.getAttribute("data-source-url") || DEFAULT_SOURCE_URL;
}

function publishGlobal(locale: string, messages: Record<string, string>, ready: boolean): void {
  const t = (key: string, fallback?: string): string => {
    const v = messages[key];
    if (v !== undefined) return v;
    return fallback ?? key;
  };
  window.pfsI18n = { t, locale, messages, ready };
}

defineComponent({
  tagName: "pfs-i18n-catalog",
  render(host, ctx) {
    // Hidden by default — purely a data-loader Element.
    host.style.display = "none";
    // Initial-Placeholder: t() liefert sofort key/fallback bis der Fetch durch
    // ist. Verhindert dass Konsumenten gegen undefined defensiven mussen.
    if (!window.pfsI18n) {
      publishGlobal(readLocale(host), {}, false);
    }

    // load(locale): fetcht den Catalog + speist BEIDE Ziele — den reaktiven
    // i18n-Store (setCatalog → withI18n-Konsumenten re-rendern) UND den Legacy-
    // window.pfsI18n-Global (Back-Compat für Nicht-WC-Konsumenten). Kein Cache
    // nötig (läuft je Locale einmal / bei Locale-Switch).
    const load = (locale: string): void => {
      const url = new URL(readSourceUrl(host), window.location.origin);
      url.searchParams.set("locale", locale);
      void apiFetch<CatalogResponse>(url.toString())
        .then((data) => {
          const loaded = data.locale || locale;
          const messages = data.messages ?? {};
          setCatalog(loaded, messages);
          publishGlobal(loaded, messages, true);
          host.dispatchEvent(
            new CustomEvent(LOADED_EVENT, {
              bubbles: true,
              composed: true,
              detail: { locale: loaded, messages },
            }),
          );
        })
        .catch((error: unknown) => {
          host.dispatchEvent(
            new CustomEvent(ERROR_EVENT, { bubbles: true, composed: true, detail: { error } }),
          );
        });
    };

    load(readLocale(host));

    // Live-Locale-Switch: der `<pfs-locale-toggle>` broadcastet einen Locale-
    // Request → den Catalog für die neue Locale nachladen → setCatalog → alle
    // withI18n-WCs re-rendern (ohne Full-Reload).
    const onLocaleRequest = (e: Event): void => {
      const detail = (e as CustomEvent).detail as { locale?: string } | undefined;
      const next = detail?.locale;
      if (next) load(next);
    };
    document.body?.addEventListener(PFS_I18N_LOCALE_REQUEST_EVENT, onLocaleRequest);
    ctx.cleanup.push(() =>
      document.body?.removeEventListener(PFS_I18N_LOCALE_REQUEST_EVENT, onLocaleRequest),
    );
  },
});
